import type { BetterFetch } from "@better-fetch/fetch";
import type { Store } from "@di-devs/better-feature/types";
import { atom } from "nanostores";
import { memberAttributeEndpointContract } from "../../shared/contracts/memberAttribute.contracts";
import {
	createMemberAttributeMutationRequestSchema,
	updateMemberAttributeMutationRequestSchema,
	updateMemberAttributeOrderMutationRequestSchema,
} from "../../shared/schemas/memberAttributeApi.schemas";
import type {
	CreateMemberAttributeRequest,
	MemberAttribute,
	UpdateMemberAttributeOrderRequest,
	UpdateMemberAttributeRequest,
} from "../../shared/types/memberAttributeApi.types";
import {
	createQuerySetMap,
	extractListeners,
	extractQueries,
	extractSignals,
} from "../shared/queryFactory";

export function createMemberAttributePlugin(
	$fetch: BetterFetch,
	_$store?: Store,
) {
	const querySets = createQuerySetMap(memberAttributeEndpointContract, $fetch);

	// Define atoms
	const $memberAttributes = atom<MemberAttribute[]>([]);
	const $selectedMemberAttribute = atom<MemberAttribute | null>(null);
	const $isLoading = atom<boolean>(false);
	const $error = atom<string | null>(null);

	return {
		signals: extractSignals(querySets),
		queries: extractQueries(querySets),
		listeners: extractListeners(querySets),
		atoms: {
			$memberAttributes,
			$selectedMemberAttribute,
			$isLoading,
			$error,
		},
		actions: {
			async listMemberAttributes() {
				const result = await $fetch<
					typeof memberAttributeEndpointContract.listMemberAttributes.responseType
				>(memberAttributeEndpointContract.listMemberAttributes.path, {
					method: memberAttributeEndpointContract.listMemberAttributes.method,
				});
				// if (result?.data) {
				// 	$memberAttributes.set(result?.data.data);
				// }
				return result;
			},

			async createMemberAttribute(data: CreateMemberAttributeRequest) {
				const validatedData =
					createMemberAttributeMutationRequestSchema.parse(data);
				const result = await $fetch<
					typeof memberAttributeEndpointContract.createMemberAttribute.responseType
				>(memberAttributeEndpointContract.createMemberAttribute.path, {
					method: memberAttributeEndpointContract.createMemberAttribute.method,
					body: validatedData,
				});

				// Refresh the list after creation
				// await this.listMemberAttributes();

				return result;
			},

			async updateMemberAttribute(
				memberAttributeId: number,
				data: UpdateMemberAttributeRequest,
			) {
				const validatedData =
					updateMemberAttributeMutationRequestSchema.parse(data);
				const result = await $fetch<
					typeof memberAttributeEndpointContract.updateMemberAttribute.responseType
				>(
					memberAttributeEndpointContract.updateMemberAttribute.path.replace(
						":memberAttributeId",
						memberAttributeId.toString(),
					),
					{
						method:
							memberAttributeEndpointContract.updateMemberAttribute.method,
						body: validatedData,
					},
				);

				// Update the selected attribute if it's the one being updated
				// const currentSelected = $selectedMemberAttribute.get();
				// if (
				// 	currentSelected &&
				// 	currentSelected.memberAttributeId === memberAttributeId
				// ) {
				// 	$selectedMemberAttribute.set({ ...currentSelected, ...data });
				// }

				// Refresh the list after update
				// await this.listMemberAttributes();

				return result;
			},

			async updateMemberAttributeOrder(
				data: UpdateMemberAttributeOrderRequest,
			) {
				const validatedData =
					updateMemberAttributeOrderMutationRequestSchema.parse(data);
				const result = await $fetch<
					typeof memberAttributeEndpointContract.updateMemberAttributeOrder.responseType
				>(memberAttributeEndpointContract.updateMemberAttributeOrder.path, {
					method:
						memberAttributeEndpointContract.updateMemberAttributeOrder.method,
					body: validatedData,
				});

				// Refresh the list after order update
				// await this.listMemberAttributes();

				return result;
			},

			async deleteMemberAttribute(memberAttributeId: number) {
				const result = await $fetch<
					typeof memberAttributeEndpointContract.deleteMemberAttribute.responseType
				>(
					memberAttributeEndpointContract.deleteMemberAttribute.path.replace(
						":memberAttributeId",
						memberAttributeId.toString(),
					),
					{
						method:
							memberAttributeEndpointContract.deleteMemberAttribute.method,
					},
				);

				// Clear selected attribute if it's the one being deleted
				// const currentSelected = $selectedMemberAttribute.get();
				// if (
				// 	currentSelected &&
				// 	currentSelected.memberAttributeId === memberAttributeId
				// ) {
				// 	$selectedMemberAttribute.set(null);
				// }

				// // Remove the deleted attribute from the list
				// const currentAttributes = $memberAttributes.get();
				// const updatedAttributes = currentAttributes.filter(
				// 	(attr) => attr.memberAttributeId !== memberAttributeId,
				// );
				// $memberAttributes.set(updatedAttributes);

				return result;
			},

			async getMemberAttribute(memberAttributeId: number) {
				const currentAttributes = $memberAttributes.get();
				const memberAttribute = currentAttributes.find(
					(attr) => attr.memberAttributeId === memberAttributeId,
				);

				// If not found in local state, refresh from server
				// if (!memberAttribute) {
				// 	await this.listMemberAttributes();
				// 	const refreshedAttributes = $memberAttributes.get();
				// 	memberAttribute = refreshedAttributes.find(
				// 		(attr) => attr.memberAttributeId === memberAttributeId,
				// 	);
				// }

				// if (memberAttribute) {
				// 	$selectedMemberAttribute.set(memberAttribute);
				// }

				return memberAttribute || null;
			},

			selectMemberAttribute(memberAttribute: MemberAttribute | null) {
				$selectedMemberAttribute.set(memberAttribute);
			},

			setLoading(isLoading: boolean) {
				$isLoading.set(isLoading);
			},

			setError(error: string | null) {
				$error.set(error);
			},
		},
		querySets, // Expose the raw querySets with proper typing
	};
}
